import {
  situationCardDetailSchema,
  situationCardListSchema,
  situationPopularSourcesSchema,
  situationSourcePageSchema,
  situationVersionDetailSchema,
  situationVersionListSchema,
  type SituationCardDetail,
  type SituationRole,
  type SituationSlotKey,
  type SituationSourceKind,
} from "@peer-start/platform-contracts";
import { request } from "../../prototype/api/client.js";

export interface SituationCardListParams {
  page: number;
  q: string;
  role: "" | "PO" | "VO" | "MBO";
  status: "" | "published" | "concept" | "review" | "processing";
}

export interface SituationSourceParams {
  page: number;
  q: string;
  type: "" | SituationSourceKind;
  seed?: number;
}

function query(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string" || typeof value === "number") {
      if (value !== "") search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export function fetchSituationCards(params: SituationCardListParams) {
  return request(`/api/admin/situation-cards${query(params)}`, situationCardListSchema);
}

export function resetSituationCards() {
  return request("/api/admin/situation-cards/reset", situationCardListSchema, {
    method: "POST",
    body: "{}",
  });
}

export function fetchSituationCard(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}`, situationCardDetailSchema);
}

export function fetchPopularSituationSources() {
  return request("/api/admin/situation-card-sources/popular", situationPopularSourcesSchema);
}

export function fetchSituationSources(params: SituationSourceParams) {
  return request(`/api/admin/situation-card-sources${query(params)}`, situationSourcePageSchema);
}

export function fetchSituationVersions(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}/versions`, situationVersionListSchema);
}

export function fetchSituationVersion(cardId: string, version: number) {
  return request(`/api/admin/situation-cards/${cardId}/versions/${version}`, situationVersionDetailSchema);
}

function sendSources(path: `/${string}`, sourceIds: string[]) {
  return request(path, situationCardDetailSchema, { method: "PUT", body: JSON.stringify({ sourceIds }) });
}

export function replaceCardSources(cardId: string, sourceIds: string[]) {
  return sendSources(`/api/admin/situation-cards/${cardId}/sources`, sourceIds);
}

export function replaceCardRoles(cardId: string, roles: readonly SituationRole[]) {
  return request(`/api/admin/situation-cards/${cardId}/roles`, situationCardDetailSchema, {
    method: "PUT",
    body: JSON.stringify({ roles }),
  });
}

export function replaceSlotSources(cardId: string, slotKey: SituationSlotKey, sourceIds: string[]) {
  return sendSources(
    `/api/admin/situation-cards/${cardId}/slots/${encodeURIComponent(slotKey)}/sources`,
    sourceIds,
  );
}

export function generateSituationCard(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}/generate`, situationCardDetailSchema, {
    method: "POST",
    body: "{}",
  });
}

export function omitSituationSources(cardId: string, sourceIds: readonly string[]) {
  return request(`/api/admin/situation-cards/${cardId}/sources/omit`, situationCardDetailSchema, {
    method: "POST",
    body: JSON.stringify({ sourceIds }),
  });
}

export function publishSituationCard(cardId: string, sourceIds: readonly string[] = []) {
  return request(`/api/admin/situation-cards/${cardId}/publish`, situationCardDetailSchema, {
    method: "POST",
    body: JSON.stringify({ sourceIds }),
  });
}

export function approveSituationCard(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}/approve`, situationCardDetailSchema, {
    method: "POST",
    body: "{}",
  });
}

export function beginSituationCardEdit(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}/edit`, situationCardDetailSchema, {
    method: "POST",
    body: "{}",
  });
}

export function reopenSituationCard(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}/reopen`, situationCardDetailSchema, {
    method: "POST",
    body: "{}",
  });
}

export function deleteSituationConcept(cardId: string) {
  return request(`/api/admin/situation-cards/${cardId}/concept`, situationCardDetailSchema, {
    method: "DELETE",
    body: "{}",
  });
}

export function restoreSituationVersion(cardId: string, version: number) {
  return request(
    `/api/admin/situation-cards/${cardId}/versions/${version}/restore`,
    situationCardDetailSchema,
    {
      method: "POST",
      body: "{}",
    },
  );
}

export type { SituationCardDetail };
